<template>
  <div
    ref="layersOperatorRef"
    class="relative"
    :style="{
      paddingTop: `${gapY}px`,
      paddingBottom: `${gapY}px`
    }"
    @contextmenu="handleContextmenu"
    @mousedown="handleMousedown"
    @mousemove="handleMove"
    @mouseleave="handleMouseleave"
    @click="handleClick"
  >
    <slot></slot>

    <div
      class="w-full h-[2px] absolute left-0 top-2.5 overflow-hidden pointer-events-none"
      :class="[isDragging && !hideBar ? 'block' : 'hidden']"
      :style="{
        top: `${gapY}px`,
        transform: `translateY(${bar.index * layerHeight}px)`
      }"
    >
      <div
        class="h-full bg-[#0D84F2]"
        :style="{
          transform: `translateX(${15 + bar.level * 24}px`
        }"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, inject, computed, watch } from 'vue';
import {
  findClosest,
  findClosestIndex,
  limitNumber
} from '@/utils/handle-number';
import { useEditorStore } from '@/store/use-editor-store';

const editorStore = useEditorStore();

const props = withDefaults(
  defineProps<{
    layerHeight?: number;
    availableLevels?: Set<number>[] | undefined;
    hideBar?: boolean;
    dragOn?: boolean | ((positionInfo: LayerOperationInfo) => boolean);
    dragLevel?: number;
    getIndex: (toLayerTop: number) => number;
    gapY?: number;
  }>(),
  {
    layerHeight: 40,
    hideBar: false,
    dragOn: true,
    availableLevels: undefined,
    dragLevel: 0,
    gapY: 10
  }
);

const emit = defineEmits<{
  (event: 'mousedown', positionInfo: LayerOperationInfo, e: MouseEvent): void;
  (event: 'click', positionInfo: LayerOperationInfo, e: MouseEvent): void;
  (event: 'hovermove', positionInfo: LayerOperationInfo, e: MouseEvent): void;
  (event: 'mouseleave'): void;
  (event: 'layersScrollEnd', positionInfo: LayerOperationInfo): void;
  (event: 'contextmenu', positionInfo: LayerOperationInfo, e: MouseEvent): void;
  (event: 'dragstart', positionInfo: LayerOperationInfo, e: MouseEvent): void;
  (
    event: 'drag',
    cursorIndex: number,
    movingIndex: number,
    level: number,
    e: MouseEvent
  ): void;
  (event: 'dragend', index: number, level: number): void;
  (event: 'longstay', index: number): void;
}>();

const layersOperatorRef = ref<HTMLElement>();
const bar = reactive({ index: -1, level: 0 });
const isDragging = ref(false);
let dragstartLeft = 0;
const lastPosition = { x: 0, y: 0 };
const checkLongstay = getCheckLongStay((index: number) => {
  emit('longstay', index);
});

const layersScrollTop = computed(() => editorStore.layersScrollTop);
const layersRect = inject<{ top: number; left: number }>('layersRect');

// 鼠标保持不动，滚动图层面板之后，需要知道鼠标对应的最新 PositionInfo
watch(
  () => layersScrollTop?.value,
  () => {
    const info = getPositionInfo(lastPosition.x, lastPosition.y);
    emit('layersScrollEnd', info);
  }
);

function handleMouseleave() {
  emit('mouseleave');
}

function handleMove(e: MouseEvent) {
  if (isDragging.value) return;

  const info = getPositionInfo(e.pageX, e.pageY);
  emit('hovermove', info, e);

  lastPosition.x = e.pageX;
  lastPosition.y = e.pageY;
}

function handleClick(e: MouseEvent) {
  const info = getPositionInfo(e.pageX, e.pageY);
  emit('click', info, e);
}

function handleMousedown(e: MouseEvent) {
  // 排除右键、中键等情况
  if (e.button !== 0) return;

  const info = getPositionInfo(e.pageX, e.pageY);
  emit('mousedown', info, e);

  if (typeof props.dragOn === 'function' ? props.dragOn(info) : props.dragOn) {
    // 绑到 window 上以 cover 指针到面板外或窗口外的情况
    window.addEventListener('mousemove', mousemove);
    window.addEventListener('mouseup', mouseup);
  }
}

function mouseup() {
  if (isDragging.value) {
    // 相当于dragend
    emit('dragend', bar.index, bar.level);
    isDragging.value = false;
    checkLongstay.reset();

    // 防止 dragstart 时闪一下
    bar.index = -1;
  }

  window.removeEventListener('mousemove', mousemove);
  window.removeEventListener('mouseup', mouseup);
}

function mousemove(e: MouseEvent) {
  if (!isDragging.value) {
    // 相当于dragstart
    isDragging.value = true;
    dragstartLeft = e.pageX;

    const info = getPositionInfo(e.pageX, e.pageY);
    emit('dragstart', info, e);
  } else {
    // 相当于drag
    if (
      props.availableLevels &&
      layersScrollTop.value !== undefined &&
      e.pageY &&
      layersRect
    ) {
      let cursorIndex = limitNumber(
        (e.pageY - layersRect.top - props.gapY + layersScrollTop.value) /
          props.layerHeight,
        0,
        props.availableLevels.length - 1
      );

      checkLongstay.occur(cursorIndex);

      const movingIndex = findClosestIndex(
        cursorIndex,
        props.availableLevels,
        (item) => item.size !== 0
      );

      if (movingIndex >= 0) {
        bar.index = movingIndex;
      }

      // 确定当前 level
      const userLevel = (e.pageX - dragstartLeft) / 24 + props.dragLevel;
      const slowLevel =
        userLevel >= props.dragLevel
          ? Math.floor(userLevel)
          : Math.ceil(userLevel);

      bar.level =
        findClosest(slowLevel, [...props.availableLevels[bar.index]]) ?? 0;

      emit('drag', cursorIndex, movingIndex, bar.level, e);
    }
  }
}

function handleContextmenu(e: MouseEvent) {
  const info = getPositionInfo(e.pageX, e.pageY);
  emit('contextmenu', info, e);
}

function getPositionInfo(pageX: number, pageY: number): LayerOperationInfo {
  const result: LayerOperationInfo = {
    offsetX: 0,
    offsetY: 0,
    index: -1
  };
  if (layersScrollTop.value !== undefined && layersRect) {
    const toLayerTop =
      pageY - layersRect.top - props.gapY + layersScrollTop.value;
    result.index = props.getIndex(toLayerTop);
    result.offsetX = pageX - layersRect.left;
    result.offsetY = toLayerTop % props.layerHeight;
  } else {
    result.index = -1;
  }

  return result;
}

function getCheckLongStay<T>(callback: (data: T) => void, duration = 1000) {
  let timeout: NodeJS.Timeout | undefined;

  return {
    occur: (data: T) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        callback(data);
        timeout = undefined;
      }, duration);
    },
    reset: () => {
      clearTimeout(timeout);
    }
  };
}
</script>
